import { ScreenSharePresets } from 'livekit-client';
import type {
  ScalabilityMode,
  ScreenShareCaptureOptions,
  TrackPublishOptions,
  VideoCodec,
} from 'livekit-client';

import { SVC_SCALABILITY_MODE, vp9SvcModes } from './publishQuality';

/**
 * M1 screen-share publish quality: picks the screen track's publish path from the same
 * browser video send capabilities the camera decision reads, and derives the
 * livekit-client capture/publish options for it.
 *
 * Decision (verified against livekit-client 2.22.3 installed types and bundle):
 *  - SVC VP9 is preferred for the screen track wherever the browser can encode it
 *    (same `vp9SvcModes` probe as the camera path). `videoCodec: 'vp9'` plus
 *    `scalabilityMode: 'L3T3_KEY'` with `simulcast: false` makes the SDK publish one
 *    1080p15 stream carrying three spatial layers; `backupCodec` (default true)
 *    publishes a single-layer VP8 backup track for subscribers that cannot decode
 *    VP9 SVC (the SDK disables backup simulcast for screen shares itself).
 *  - Browsers without SVC-capable VP9 (Firefox, Safari) publish simulcast: the primary
 *    layer at 1080p15 (`ScreenSharePresets.h1080fps15`: 2.5 Mbps, priority medium)
 *    with a 360p15 low layer (`h360fps15`: 400 kbps) — two layers fit text-heavy
 *    screen content on slow downlinks while keeping the full resolution available.
 *    Codec VP8, H264 when VP8 is absent, mirroring the camera matrix.
 *  - No recognisable codec set (capabilities missing entirely, jsdom) or nothing
 *    simulcast-capable publishes a single 1080p15 layer — screen sharing stays
 *    possible even when the capability probe cannot run.
 *
 * Capture is capped at 1920x1080@15 (`contentHint: 'detail'` so the encoder favours
 * sharpness for text/UI); the SDK already defaults screen shares to
 * `degradationPreference: 'maintain-resolution'`, so it is not set here.
 */

/** Screen-share publish path identifier. */
export type ScreenShareMode = 'vp9-svc' | 'simulcast' | 'single';

/** The publish path chosen for the screen track, with the reason recorded for debugging. */
export interface ScreenShareDecision {
  readonly mode: ScreenShareMode;
  /** Simulcast only: the codec to publish the screen track with. */
  readonly codec?: VideoCodec;
  /** SVC only: the scalabilityMode handed to livekit-client. */
  readonly scalabilityMode?: ScalabilityMode;
  readonly rationale: string;
}

const SINGLE_UNAVAILABLE: ScreenShareDecision = {
  mode: 'single',
  rationale: 'video send capabilities unavailable; single 1080p15 layer',
};

/**
 * Picks the screen-share publish path from video send capabilities. Pure: every input
 * shape (VP9 SVC-capable, VP8/H264 only, empty/absent, nothing recognisable) yields a
 * typed decision with a rationale.
 */
export function decideScreenSharePath(
  capabilities: RTCRtpCapabilities | null | undefined,
): ScreenShareDecision {
  const codecs = capabilities?.codecs;
  if (codecs === undefined || codecs.length === 0) {
    return SINGLE_UNAVAILABLE;
  }
  const modes = vp9SvcModes(codecs);
  if (modes !== null) {
    return {
      mode: 'vp9-svc',
      scalabilityMode: SVC_SCALABILITY_MODE,
      rationale: `VP9 profile 0 with 3-spatial-layer modes (${modes})`,
    };
  }
  if (codecs.some((codec) => codec.mimeType === 'video/VP8')) {
    return {
      mode: 'simulcast',
      codec: 'vp8',
      rationale: 'VP9 SVC unavailable; 1080p15 primary with a 360p15 low layer',
    };
  }
  if (codecs.some((codec) => codec.mimeType === 'video/H264')) {
    return {
      mode: 'simulcast',
      codec: 'h264',
      rationale: 'VP9 SVC unavailable and VP8 absent; H264 1080p15/360p15 simulcast',
    };
  }
  return {
    mode: 'single',
    rationale: 'no simulcast-capable codec recognised; single 1080p15 layer',
  };
}

/** Capture options for the screen track: capped at 1920x1080@15, detail-favoured. */
export function screenShareCaptureOptions(): ScreenShareCaptureOptions {
  return {
    resolution: { width: 1920, height: 1080, frameRate: 15 },
    contentHint: 'detail',
  };
}

/** Publish options handed to setScreenShareEnabled for the decided path. */
export function screenSharePublishOptions(decision: ScreenShareDecision): TrackPublishOptions {
  if (decision.mode === 'vp9-svc') {
    return {
      videoCodec: 'vp9',
      scalabilityMode: decision.scalabilityMode ?? SVC_SCALABILITY_MODE,
      // One SVC stream carries the layers; rid simulcast must stay off for L3Tx modes.
      simulcast: false,
      screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
      // backupCodec stays at the SDK default (true): a single-layer VP8 track is
      // published automatically for subscribers that cannot decode VP9 SVC.
    };
  }
  if (decision.mode === 'simulcast') {
    return {
      videoCodec: decision.codec ?? 'vp8',
      simulcast: true,
      screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
      // Layers are ordered lowest to highest, as the SDK requires.
      screenShareSimulcastLayers: [ScreenSharePresets.h360fps15],
    };
  }
  return {
    simulcast: false,
    screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
  };
}
